/**
 * M10 P4b4a (plan docs/plans/2026-10-15-m10-p4b.md §3.3; hull tiers spec §4.5): the operator conversion kits.
 *
 * - **Data.** `src/data/kits.json` holds the nine kits (Zod-checked here: eligible hulls and bands, overlay, loadout,
 *   quirk, livery, name pattern); `src/data/kit-parts.json` (written by `tools/blender/greybox_m10_kits.py`) holds
 *   each operator's part GLB and every part's mounts per base hull. `loadKits` joins the two and resolves each kit
 *   loadout against the asset manifest and weapons, once.
 * - **The pick.** `kitFor(faction, hullId, agentId)` hashes the strategic agent's stable serial id (`LocalAgent.id`,
 *   `ag-<seq>`, saved as `counters.agentSeq`; review m15) with FNV-1a (plus a 32-bit finaliser so short sequential ids
 *   spread) and picks among the operator's agent-picked kits for that hull. No RNG draw and nothing saved: the same
 *   agent gets the same kit after promote → demote → promote and after save → load. Only the Mutual (`mutual`) and
 *   the Wreckers (`raider`) operate kits; every other faction gets null.
 * - **The loadout.** `applyKitLoadout(def, kit)` is the one loader (P4b4b's overlay and P4b6a's Harpoon raid call it;
 *   review m8): the kit's resolved mounts are added to the base def's, and its torpedo rounds with them.
 * - **The kit def** (M10 P4b4b): `kitDef(base, kit)` is the base def with the kit's loadout, then its local overlay
 *   (`kits.json overlay`: maxSpeed, shield), then its quirk (one M9 flaw bit: that flaw's stat × mult, which is what
 *   `traitMults` gives for it). It keeps the base def's `id`, so every id reader (the strategic hull, its `strength`,
 *   crime value, wrecks) reads the base hull: kits are local-only and off-screen strength is unchanged. The utility
 *   mount, repair cost and detection overlays have no local AI reader in 1.0; they stay on the kit for the player
 *   refit. The Wreckers ram plate is visual only (P4b4a's part set).
 * - **The slots** (M10 P4b4c): `addKitSlots(defs)` appends every (kit, base hull) def once, at boot, right after
 *   `loadGameData` and before the sim, the M2 data and the career exist. Each is built from a pristine copy of its
 *   base, so the career's rewrite of the start slot never reaches it, and every table sized at init (salvage layouts
 *   and models, wreck views, sockets, ship views, crime values) covers it. Indices are stable run to run. The
 *   lookups (`kitDefIndex`, `kitBaseOf`) are in `kitSlots.ts`, which imports no kit data.
 *
 * Message rate or rarer (boot, a manifest, a spawn, a promotion): nothing here runs per frame.
 */
import { z } from 'zod'
import type { AssetsFile, ModelEntry, SocketEntry } from '../data/assetSchema'
import { loadGameData, type GameData } from '../data'
import hulksJson from '../data/hulks.json'
import kitPartsJson from '../data/kit-parts.json'
import kitsJson from '../data/kits.json'
import { loadoutItemSchema, shipBandSchema, type LoadoutItem, type ResolvedMount, type ShipBand, type ShipDef, type WeaponsData } from '../data/schemas'
import { composeMuzzle } from '../data/shipResolve'
import { hulksConfig } from '../hulks/data'
import { FACTION_IDS as LOOK_IDS, type FactionId as LookId } from '../render/contracts'
import { kitSlotRegistry } from './kitSlots'

/** The kit operators by strategic faction id: the Mutual and the Wreckers (`raider`). */
export const KIT_OPERATORS = ['mutual', 'raider'] as const
export type KitOperator = (typeof KIT_OPERATORS)[number]
/** kit-parts.json names the operator sets by their people, kits.json by faction id. */
const PART_SET: Readonly<Record<KitOperator, 'mutual' | 'wreckers'>> = {
  mutual: 'mutual',
  raider: 'wreckers',
}
const NAME_TOKENS = ['name', 'hull', 'kit', 'tally'] as const

const vec3 = z.tuple([z.number(), z.number(), z.number()])
const kitMountSchema = z
  .object({
    socket: z.string().optional(),
    offset: vec3.optional(),
    mirror: z.boolean(),
    scale: z.number().positive(),
  })
  .refine((m) => (m.socket === undefined) !== (m.offset === undefined), {
    message: 'a mount is a socket or an offset',
  })
export type KitMount = z.infer<typeof kitMountSchema>

/** `kit-parts.json` (the P4b2d mount table). */
export const kitPartsFileSchema = z.object({
  comment: z.string(),
  sets: z.record(
    z.enum(['mutual', 'wreckers']),
    z.object({
      asset: z.string().min(1),
      kits: z.record(
        z.string(),
        z.object({
          hulls: z.array(z.string()),
          pendingHulls: z.array(z.string()),
          parts: z
            .array(
              z.object({
                con: z.string().regex(/^CON_[A-Z0-9]+(?:_[A-Z0-9]+)*$/),
                face: z.enum(['flank', 'dorsal', 'ventral', 'bow']),
                mounts: z.record(z.string(), z.array(kitMountSchema).min(1)),
              }),
            )
            .min(1),
        }),
      ),
    }),
  ),
})
export type KitPartsFile = z.infer<typeof kitPartsFileSchema>

const overlaySchema = z
  .object({
    utilityMounts: z.number().int().min(0).max(4).optional(),
    maxSpeedMult: z.number().positive().optional(),
    shieldMult: z.number().positive().optional(),
    detectionMult: z.number().positive().optional(),
    repairCostMult: z.number().positive().optional(),
  })
  .strict()

const kitEntrySchema = z
  .object({
    id: z.string().regex(/^[a-z]+$/),
    name: z.string().min(1),
    operator: z.enum(KIT_OPERATORS),
    hulls: z.array(z.string().min(1)),
    bands: z.array(shipBandSchema).min(1),
    agentPick: z.boolean(),
    overlay: overlaySchema,
    loadout: z.record(z.string(), z.array(loadoutItemSchema).min(1)),
    quirk: z.object({ flaw: z.string().min(1), label: z.string().min(1) }).strict(),
    livery: z.enum(LOOK_IDS as unknown as [LookId, ...LookId[]]),
    namePattern: z.string().min(1),
  })
  .strict()
export type KitEntry = z.infer<typeof kitEntrySchema>

/** What the kits schema checks hull names and quirks against. */
export interface KitSchemaContext {
  /** The size band of a ships.json def, undefined for an unknown def id. */
  hullBand(defId: string): ShipBand | undefined
  /** The M9 flaw ids in bit order (`hulks.json flaws`). */
  readonly flawIds: readonly string[]
}

const kitsFileBase = z.object({ comment: z.string(), kits: z.array(kitEntrySchema) }).strict()
export type KitsFile = z.infer<typeof kitsFileBase>

/** The kits.json schema: per kit, every named hull is an eligible hull with a def in an eligible band. */
export function kitsFileSchemaFor(ctx: KitSchemaContext): z.ZodType<KitsFile> {
  return kitsFileBase.superRefine((file, issue) => {
    const ids = new Set<string>()
    file.kits.forEach((k, i) => {
      const at = (msg: string, ...path: (string | number)[]): void =>
        issue.addIssue({
          code: 'custom',
          message: `kit ${k.id}: ${msg}`,
          path: ['kits', i, ...path],
        })
      if (ids.has(k.id)) at('duplicate id', 'id')
      ids.add(k.id)
      if (new Set(k.hulls).size !== k.hulls.length) at('a hull is listed twice', 'hulls')
      for (const h of k.hulls) {
        const band = ctx.hullBand(h)
        if (band === undefined) at(`hull ${h} has no ships.json def`, 'hulls')
        else if (!k.bands.includes(band)) at(`hull ${h} is band ${band}, outside its eligible bands`, 'hulls')
      }
      for (const [h, items] of Object.entries(k.loadout)) {
        if (!k.hulls.includes(h)) at(`loadout names ${h}, outside its eligible hulls`, 'loadout', h)
        if (new Set(items.map((l) => l.socket)).size !== items.length) at(`loadout on ${h} puts two items on one socket`, 'loadout', h)
      }
      if (!ctx.flawIds.includes(k.quirk.flaw)) at(`quirk flaw ${k.quirk.flaw} is not an M9 flaw`, 'quirk', 'flaw')
      for (const m of k.namePattern.matchAll(/\{([^}]*)\}/g)) {
        if (!(NAME_TOKENS as readonly string[]).includes(m[1])) at(`name pattern token {${m[1]}} is unknown`, 'namePattern')
      }
    })
  })
}

/** One part of a kit: its `CON_` socket on the operator's part GLB (with that socket's x slot) and its mounts per hull. */
export interface KitPart {
  readonly con: string
  readonly face: 'flank' | 'dorsal' | 'ventral' | 'bow'
  /** The part's x slot on the set GLB: a LOD vertex belongs to it when x0 ≤ x < x1 (P4b2d layout). */
  readonly x0: number
  readonly x1: number
  /** The `CON_` socket position on the set GLB (the part's attach point). */
  readonly at: readonly [number, number, number]
  readonly mounts: ReadonlyMap<string, readonly KitMount[]>
}

/** A kit's loadout on one base hull, resolved against the manifest and weapons. */
export interface KitLoadout {
  readonly mounts: readonly ResolvedMount[]
  readonly torpedoRounds: number
}

/** A resolved kit (immutable). */
export interface Kit {
  readonly id: string
  readonly name: string
  readonly operator: KitOperator
  readonly hulls: readonly string[]
  readonly bands: readonly ShipBand[]
  readonly agentPick: boolean
  readonly overlay: Readonly<KitEntry['overlay']>
  readonly quirk: {
    readonly flaw: string
    readonly bit: number
    readonly label: string
  }
  readonly livery: LookId
  readonly namePattern: string
  /** The operator's part GLB (`ship.kit_<set>_block`). */
  readonly partAsset: string
  readonly parts: readonly KitPart[]
  /** Per eligible hull with a loadout; absent = the base loadout. */
  readonly loadouts: ReadonlyMap<string, KitLoadout>
}

export interface KitTable {
  readonly kits: readonly Kit[]
  /** Operator → base hull def id → the agent-picked kits for it (data order). */
  readonly picks: ReadonlyMap<string, ReadonlyMap<string, readonly Kit[]>>
  byId(id: string): Kit | undefined
}

function modelEntry(assets: AssetsFile, id: string, what: string): ModelEntry {
  const m = assets.models.find((e) => e.id === id)
  if (m === undefined) throw new Error(`kits: ${what}: asset ${id} is missing from assets.json`)
  return m
}

function socketEntry(model: ModelEntry, name: string, what: string): SocketEntry {
  const s = model.sockets.find((e) => e.name === name)
  if (s === undefined) throw new Error(`kits: ${what}: socket ${name} is missing on ${model.id}`)
  return s
}

/** One kit loadout on `def` resolved like `shipResolve.ts` resolves a def's loadout (the module muzzles in ship space). */
function resolveLoadout(def: ShipDef, items: readonly LoadoutItem[], assets: AssetsFile, weapons: WeaponsData, kitId: string): KitLoadout {
  const hull = modelEntry(assets, def.assetId, `${kitId} on ${def.id}`)
  let torpedoRounds = 0
  const mounts = items.map((item): ResolvedMount => {
    const what = `${kitId} on ${def.id}`
    const socket = socketEntry(hull, item.socket, what)
    const module = modelEntry(assets, item.module, what)
    if (module.class !== 'hardpoint') throw new Error(`kits: ${what}: ${item.module} is not a hardpoint`)
    const weapon = weapons.weapons.findIndex((w) => w.id === item.weapon)
    if (weapon < 0) throw new Error(`kits: ${what}: weapon ${item.weapon} is missing from weapons.json`)
    const w = weapons.weapons[weapon]
    if (w.kind === 'torpedo') torpedoRounds += w.rounds
    const muzzles = module.sockets
      .filter((s) => /^HP_MUZZLE_\d+$/.test(s.name))
      .sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))
      .map((m) => composeMuzzle(socket, m))
    if (muzzles.length === 0) throw new Error(`kits: ${what}: ${item.module} has no HP_MUZZLE_ sockets`)
    return {
      weapon,
      group: item.group,
      slot: item.slot,
      socket: item.socket,
      module: item.module,
      muzzles,
    }
  })
  return { mounts, torpedoRounds }
}

function partsOf(entry: KitEntry, parts: KitPartsFile, assets: AssetsFile): { asset: string; parts: KitPart[] } {
  const set = parts.sets[PART_SET[entry.operator]]
  const table = set?.kits[entry.id]
  if (set === undefined || table === undefined) throw new Error(`kits: ${entry.id}: no part set in kit-parts.json`)
  const glb = modelEntry(assets, set.asset, entry.id)
  const listed = [...table.hulls].sort().join()
  if (listed !== [...entry.hulls].sort().join()) throw new Error(`kits: ${entry.id}: hulls [${entry.hulls.join()}] differ from kit-parts.json [${table.hulls.join()}]`)
  const out = table.parts.map((p): KitPart => {
    const con = socketEntry(glb, p.con, entry.id)
    for (const h of Object.keys(p.mounts)) if (!entry.hulls.includes(h)) throw new Error(`kits: ${entry.id}: ${p.con} mounts on ${h}, outside its eligible hulls`)
    for (const h of entry.hulls) if (p.mounts[h] === undefined) throw new Error(`kits: ${entry.id}: ${p.con} has no mount on ${h}`)
    return {
      con: p.con,
      face: p.face,
      x0: Number(con.params.x0),
      x1: Number(con.params.x1),
      at: [con.position[0], con.position[1], con.position[2]],
      mounts: new Map(Object.entries(p.mounts)),
    }
  })
  return { asset: set.asset, parts: out }
}

/** Parses kits.json, joins kit-parts.json and resolves the loadouts. Throws naming the kit and the bad reference. */
export function loadKits(data: Pick<GameData, 'ships' | 'assets' | 'weapons'>, raw: unknown = kitsJson, rawParts: unknown = kitPartsJson): KitTable {
  const defs = new Map(data.ships.map((d) => [d.id, d]))
  const flawIds = hulksJson.flaws.map((f) => f.id)
  const file = kitsFileSchemaFor({
    hullBand: (id) => defs.get(id)?.sizeBand,
    flawIds,
  }).parse(raw)
  const parts = kitPartsFileSchema.parse(rawParts)
  const kits = file.kits.map((e): Kit => {
    const joined = partsOf(e, parts, data.assets)
    const loadouts = new Map<string, KitLoadout>()
    for (const [h, items] of Object.entries(e.loadout)) {
      const def = defs.get(h)
      if (def !== undefined) loadouts.set(h, resolveLoadout(def, items, data.assets, data.weapons, e.id))
    }
    return {
      id: e.id,
      name: e.name,
      operator: e.operator,
      hulls: e.hulls,
      bands: e.bands,
      agentPick: e.agentPick,
      overlay: e.overlay,
      quirk: {
        flaw: e.quirk.flaw,
        bit: flawIds.indexOf(e.quirk.flaw),
        label: e.quirk.label,
      },
      livery: e.livery,
      namePattern: e.namePattern,
      partAsset: joined.asset,
      parts: joined.parts,
      loadouts,
    }
  })
  const picks = new Map<string, Map<string, Kit[]>>()
  for (const op of KIT_OPERATORS) picks.set(op, new Map())
  for (const k of kits) {
    if (!k.agentPick) continue
    const byHull = picks.get(k.operator)
    if (byHull === undefined) continue
    for (const h of k.hulls) {
      const list = byHull.get(h) ?? []
      list.push(k)
      byHull.set(h, list)
    }
  }
  const byId = new Map(kits.map((k) => [k.id, k]))
  return { kits, picks, byId: (id) => byId.get(id) }
}

let bundled: KitTable | null = null

/** The bundled kits over the bundled game data (parsed once per process, on first use). */
export function bundledKits(): KitTable {
  bundled ??= loadKits(loadGameData())
  return bundled
}

/** FNV-1a (32-bit) over the UTF-16 code units of `s`, then the murmur3 finaliser so `ag-1`, `ag-2`, … spread in the low bits. */
export function kitHash(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  h ^= h >>> 16
  h = Math.imul(h, 0x85ebca6b)
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35)
  h ^= h >>> 16
  return h >>> 0
}

/**
 * The kit agent `agentId` (`LocalAgent.id`, `ag-<seq>`; never a store slot) flies on base hull `hullId` (its ships.json
 * def id) for `faction`: one of the operator's agent-picked kits for that hull by `kitHash(agentId)`, or null (not a
 * kit operator, no kit for the hull). No RNG, nothing saved. Allocation-free.
 */
export function kitFor(faction: string, hullId: string, agentId: string, table: KitTable = bundledKits()): Kit | null {
  const list = table.picks.get(faction)?.get(hullId)
  if (list === undefined || list.length === 0) return null
  return list[kitHash(agentId) % list.length]
}

/**
 * `def` with `kit`'s loadout on it (the one kit loadout loader; P4b4b and P4b6a call it): the kit's resolved mounts
 * after the base def's and its torpedo rounds added (capped at 255, as `shipResolve.ts`). A kit without a loadout on
 * the hull returns `def` itself. Throws when `def` is not one of the kit's base hulls. Rare (a spawn).
 */
export function applyKitLoadout(def: ShipDef, kit: Kit): ShipDef {
  if (!kit.hulls.includes(def.id)) throw new Error(`kits: ${kit.id} does not fit ${def.id} (eligible: ${kit.hulls.join(', ')})`)
  const add = kit.loadouts.get(def.id)
  if (add === undefined) return def
  return {
    ...def,
    mounts: [...def.mounts, ...add.mounts],
    torpedoRounds: Math.min(def.torpedoRounds + add.torpedoRounds, 255),
  }
}

/** `base` with `kit`'s loadout, overlay and quirk (a new def; `base` is never changed). Rare (boot). */
export function kitDef(base: ShipDef, kit: Kit): ShipDef {
  const def = { ...applyKitLoadout(base, kit) }
  def.maxSpeed *= kit.overlay.maxSpeedMult ?? 1
  def.shield *= kit.overlay.shieldMult ?? 1
  const q = hulksConfig().flaws[kit.quirk.bit]
  def[q.stat] *= q.mult
  def.absMaxSpeed = Math.max(def.absMaxSpeed, def.maxSpeed * def.boostSpeedMult)
  return def
}

/** Appends one kit def per (kit, base hull) to `defs`, once per array, from pristine base copies. Returns the base def count. Rare (boot). */
export function addKitSlots(defs: ShipDef[], kits: KitTable = bundledKits()): number {
  let reg = kitSlotRegistry.get(defs)
  if (reg !== undefined) return reg.first
  kitSlotRegistry.set(defs, (reg = { bySlot: new Map(), first: defs.length, base: [] }))
  for (const kit of kits.kits)
    for (const hull of kit.hulls) {
      const base = defs.findIndex((d) => d.id === hull)
      if (base < 0) continue
      reg.bySlot.set(`${base}:${kit.id}`, defs.push(kitDef(structuredClone(defs[base]), kit)) - 1)
      reg.base.push(base)
    }
  return reg.first
}
